import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import clsx from 'clsx'
import * as CompanyEmailValidator from 'company-email-validator'
import { event } from 'nextjs-google-analytics'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import Input from '@/components/input'
import Select from '@/components/select'
import ThankYou from '@/components/thank-you'
import { CountryData } from '@/pages/api/countries'

export const isValidEmail = (email: string): boolean => {
  return CompanyEmailValidator.isCompanyEmail(email)
}

type FormData = {
  fullName: string
  country: string
  jobTitle: string
  workEmail: string
  companyName: string
}

const ContactForm = () => {
  const { data: countries, isLoading: countriesLoading } = useQuery(
    ['countries'],
    () => axios.get<CountryData>('/api/countries').then(res => res.data)
  )

  const [sentRequest, setSentRequest] = useState(false)
  const {
    control,
    handleSubmit,
    reset: resetForm,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      country: 'TR',
    },
  })

  const { mutate, isLoading, error } = useMutation(
    (data: FormData) => axios.post('/api/contact', data),
    {
      onSuccess: () => {
        resetForm()
        event('successful_form_submission', {
          category: 'Demo Request',
          label: watch('workEmail'),
          userId: watch('workEmail'),
        })
        setSentRequest(true)
        window.scrollTo(0, 0)
      },
      onError: () => {
        event('failed_form_submission', {
          category: 'Demo Request',
          label: watch('workEmail') || 'Unknown',
        })
      },
    }
  )

  const onSubmit = (data: FormData) => {
    mutate(data)
  }

  return (
    <>
      {!sentRequest && (
        <section className="relative flex w-full justify-center bg-white">
          <section className="invisible sticky top-0 flex h-screen w-0 items-center justify-center bg-brand-600 md:invisible md:w-0 lg:visible lg:w-3/5">
            <div className="mx-auto flex h-full items-center justify-center">
              <div className="flex max-w-5xl flex-col gap-10 text-brand-50">
                <h2 className="text-left text-3xl/snug font-semibold md:text-4xl/snug lg:text-5xl/snug">
                  Discover the Power of
                  <br /> Data-Driven E-commerce
                </h2>
                <p className="text-left text-xl/snug sm:text-2xl/snug md:text-3xl/snug lg:text-4xl/snug">
                  Request a demo now and learn how
                  <br /> our solutions can increase your e-commerce
                  <br />
                  performance with real-time data.
                </p>
              </div>
            </div>
          </section>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex h-full w-full flex-col justify-between gap-y-6 px-5 pt-24 md:px-10 lg:w-2/5 lg:px-20"
          >
            <div className="flex flex-col gap-y-4">
              <Controller
                control={control}
                name="fullName"
                rules={{ required: 'Please enter your full name' }}
                render={({ field }) => (
                  <Input
                    {...field}
                    required
                    maxLength={100}
                    placeholder="Enter your full name"
                    label="Full Name"
                    type="text"
                    error={errors.fullName?.message}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="workEmail"
                rules={{
                  required: 'Please enter your business email',
                  validate: value =>
                    isValidEmail(value) ||
                    'Please enter a valid business email',
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    required
                    maxLength={100}
                    placeholder="Enter your business e-mail"
                    label="Business E-mail"
                    type="email"
                    error={errors.workEmail?.message}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                rules={{ required: 'Please enter your company name' }}
                name="companyName"
                render={({ field }) => (
                  <Input
                    {...field}
                    required
                    maxLength={100}
                    placeholder="Enter your company name"
                    label="Company Name"
                    error={errors.companyName?.message}
                    type="text"
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="country"
                rules={{ required: 'Please enter your country' }}
                render={({ field }) => (
                  <Select
                    defaultValue="TR"
                    required
                    error={errors.country?.message}
                    options={countries}
                    disabled={countriesLoading}
                    value={field.value}
                    label="Country"
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="jobTitle"
                rules={{ required: 'Please enter your job title' }}
                render={({ field }) => (
                  <Input
                    {...field}
                    required
                    maxLength={100}
                    placeholder="Enter your job title"
                    label="Job Title"
                    error={errors.jobTitle?.message}
                    type="text"
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            {!!error && (
              <div className="flex rounded-xl bg-red-100 px-4 py-4 text-sm font-medium text-red-500">
                {(error as any)?.response?.data?.error}
              </div>
            )}
            <div className="bg-white py-2">
              <button
                disabled={isLoading}
                type="submit"
                className={clsx(
                  'flex w-full items-center justify-center rounded-full border border-transparent bg-orange-500 py-3 text-zinc-200 hover:bg-orange-400',
                  isLoading ? ' bg-opacity-70' : ''
                )}
              >
                {isLoading ? (
                  <div role="status">
                    <svg
                      aria-hidden="true"
                      className={clsx(
                        'mr-2 h-8 w-8 animate-spin fill-orange-600 text-gray-200',
                        isLoading ? 'fill-orange-300' : ''
                      )}
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                    <span className="sr-only">Loading...</span>
                  </div>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          </form>
        </section>
      )}
      {sentRequest ? <ThankYou /> : null}
    </>
  )
}

export default ContactForm
